import { useEffect, useState, useCallback } from "react";
import { useDistrictGuard } from "./useDistrictGuard";
import { getDistrictSetting, setDistrictSetting } from "./api";
import {
  EVENT_TYPES,
  FAMILY_STATUSES,
  SPIRITUAL_STATUSES,
  PRAYER_CATEGORIES,
  VISIT_TYPES,
  MARITAL_STATUSES,
  BAPTISM_STATUSES,
} from "./constants";

const STORAGE_KEY = "pastoral.customLists.v1";
const SETTINGS_KEY = "custom_lists";

// Default lists — mirror /app/frontend/src/lib/constants.js but authoritative for editable sections.
// Each list contains { value, label, tone? }. `tone` drives Badge colors.
export const DEFAULT_LISTS = {
  eventTypes: EVENT_TYPES,
  visitTypes: VISIT_TYPES,
  familyStatuses: FAMILY_STATUSES,
  spiritualStatuses: SPIRITUAL_STATUSES,
  maritalStatuses: MARITAL_STATUSES,
  baptismStatuses: BAPTISM_STATUSES,
  prayerCategories: PRAYER_CATEGORIES,
};

export const LIST_META = {
  eventTypes: { label: "Tipos de evento", supportsTone: false },
  visitTypes: { label: "Tipos de visita", supportsTone: false },
  familyStatuses: { label: "Estados de familia", supportsTone: true },
  spiritualStatuses: { label: "Estados espirituales", supportsTone: true },
  maritalStatuses: { label: "Estados civiles", supportsTone: false },
  baptismStatuses: { label: "Estados de bautismo", supportsTone: false },
  prayerCategories: { label: "Categorías de oración", supportsTone: true },
};

export const TONE_OPTIONS = [
  { value: "slate", label: "Gris" },
  { value: "blue", label: "Azul" },
  { value: "green", label: "Verde" },
  { value: "amber", label: "Ámbar" },
  { value: "red", label: "Rojo" },
];

// Generate a URL-friendly slug for new entry `value`s (so IDs stay stable once saved).
export function slugify(label) {
  return (label || "")
    .toString()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "item";
}

function mergeLists(defaultList, storedList) {
  const map = new Map();
  defaultList.forEach(item => map.set(item.value, item));
  storedList.forEach(item => map.set(item.value, { ...map.get(item.value), ...item }));
  return Array.from(map.values());
}

function mergeAllLists(stored) {
  const merged = {};
  for (const key in DEFAULT_LISTS) {
    merged[key] = mergeLists(DEFAULT_LISTS[key], stored[key] || []);
  }
  return merged;
}

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_LISTS;
    const parsed = JSON.parse(raw);
    return mergeAllLists(parsed);
  } catch {
    return DEFAULT_LISTS;
  }
}

function writeStored(lists) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(lists));
  window.dispatchEvent(new Event("customLists:updated"));
}

export function useCustomLists() {
  const { districtId } = useDistrictGuard();
  const [lists, setLists] = useState(readStored);

  // Hydrate from Supabase on mount — remote wins over stale localStorage
  useEffect(() => {
    if (!districtId) return;
    getDistrictSetting(districtId, SETTINGS_KEY, null)
      .then(remote => {
        if (!remote) return;
        const merged = mergeAllLists(remote);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
        setLists(merged);
      })
      .catch(() => {});
  }, [districtId]);

  // Cross-tab / cross-component sync via storage events
  useEffect(() => {
    const onChange = () => setLists(readStored());
    window.addEventListener("customLists:updated", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("customLists:updated", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const updateList = useCallback((key, items) => {
    const next = { ...lists, [key]: items };
    writeStored(next);
    setLists(next);
    if (districtId) {
      setDistrictSetting(districtId, SETTINGS_KEY, next).catch(() => {});
    }
  }, [lists, districtId]);

  const resetList = useCallback((key) => updateList(key, DEFAULT_LISTS[key]), [updateList]);

  const resetAll = useCallback(() => {
    writeStored(DEFAULT_LISTS);
    setLists(DEFAULT_LISTS);
    if (districtId) {
      setDistrictSetting(districtId, SETTINGS_KEY, DEFAULT_LISTS).catch(() => {});
    }
  }, [districtId]);

  const getList = useCallback((key) => lists[key] || DEFAULT_LISTS[key] || [], [lists]);

  const find = useCallback((key, value) =>
    getList(key).find((x) => x.value === value) || null,
  [getList]);

  return { lists, getList, find, updateList, resetList, resetAll };
}
